import { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Users,
  DollarSign,
  UserCheck,
  Building,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Separator } from "../../../../components/ui/separator";
import { Badge } from "../../../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { cn } from "../../../../lib/utils";

const statusOptions = [
  { value: "all", label: "Tous les statuts" },
  { value: "true", label: "Actif" },
  { value: "false", label: "Inactif" },
];

const employeeCountRanges = [
  { value: "all", label: "Toutes tailles" },
  { value: "0-10", label: "0-10 employés" },
  { value: "11-50", label: "11-50 employés" },
  { value: "51-100", label: "51-100 employés" },
  { value: "100+", label: "100+ employés" },
];

const managerOptions = [
  { value: "all", label: "Tous" },
  { value: "has-manager", label: "Avec manager" },
  { value: "no-manager", label: "Sans manager" },
];

const quickFilters = [
  { key: "large", label: "Grands départements", icon: Building },
  { key: "new", label: "Nouveaux (30j)", icon: Calendar },
  { key: "no-budget", label: "Sans budget", icon: DollarSign },
  { key: "no-manager", label: "Sans manager", icon: UserCheck },
];

export default function FilterSidebar({
  isOpen,
  onClose,
  filters = {},
  onFiltersChange,
  availableManagers = [],
}) {
  const [localFilters, setLocalFilters] = useState(filters || {});

  // Sync localFilters with external filter changes
  useEffect(() => {
    setLocalFilters(filters || {});
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleQuickFilter = (filterKey) => {
    let newFilters = { ...localFilters };

    switch (filterKey) {
      case "large":
        newFilters.employeeCount = "51-100";
        break;
      case "new":
        newFilters.dateRange = "30-days";
        break;
      case "no-budget":
        newFilters.budgetRange = "0-0";
        break;
      case "no-manager":
        newFilters.manager = "no-manager";
        break;
    }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {};
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    if (!localFilters || typeof localFilters !== "object") {
      return 0;
    }
    return Object.entries(localFilters).filter(([key, value]) => {
      if (key === "status" || key === "employeeCount" || key === "manager") {
        return value && value !== "all";
      }
      return value && value !== "" && value !== "all";
    }).length;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative top-0 right-0 h-full w-80 bg-white border-l lg:border-l-0 lg:border-r border-gray-200 z-50 lg:z-0",
          "transform transition-transform duration-300 ease-in-out",
          "lg:transform-none lg:transition-none",
          isOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Filtres avancés</h3>
            <div className="flex items-center gap-2">
              {getActiveFilterCount() > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {getActiveFilterCount()}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="lg:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Quick Filters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Filtres rapides</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                {quickFilters.map(({ key, label, icon: Icon }) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickFilter(key)}
                    className="justify-start text-xs h-8"
                  >
                    <Icon className="h-3 w-3 mr-2" />
                    {label}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Status Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center">
                <Building className="h-4 w-4 mr-2" />
                Statut
              </Label>
              <Select
                value={localFilters.status || "all"}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Count Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center">
                <Users className="h-4 w-4 mr-2" />
                Nombre d'employés
              </Label>
              <Select
                value={localFilters.employeeCount || "all"}
                onValueChange={(value) =>
                  handleFilterChange("employeeCount", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {employeeCountRanges.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Budget Range Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center">
                <DollarSign className="h-4 w-4 mr-2" />
                Budget (DH)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={localFilters.budgetMin || ""}
                  onChange={(e) =>
                    handleFilterChange("budgetMin", e.target.value)
                  }
                  min="0"
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={localFilters.budgetMax || ""}
                  onChange={(e) =>
                    handleFilterChange("budgetMax", e.target.value)
                  }
                  min="0"
                />
              </div>
            </div>

            {/* Manager Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center">
                <UserCheck className="h-4 w-4 mr-2" />
                Manager
              </Label>
              <Select
                value={localFilters.manager || "all"}
                onValueChange={(value) => handleFilterChange("manager", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {managerOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  <Separator />
                  {availableManagers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {manager.prenom} {manager.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Date de création
              </Label>
              <Select
                value={localFilters.dateRange || "all"}
                onValueChange={(value) =>
                  handleFilterChange("dateRange", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les dates</SelectItem>
                  <SelectItem value="7-days">7 derniers jours</SelectItem>
                  <SelectItem value="30-days">30 derniers jours</SelectItem>
                  <SelectItem value="90-days">90 derniers jours</SelectItem>
                  <SelectItem value="1-year">Cette année</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="w-full"
            >
              Effacer tous les filtres
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
